import { getDB } from "./db";
import type { OfflineSale } from "./db";

export type { OfflineSale };

export const OFFLINE_SYNC_ATOMIC_RPC_REQUIRED =
  "OFFLINE_SYNC_ATOMIC_RPC_REQUIRED";

export class OfflineSyncUnavailableError extends Error {
  readonly code = OFFLINE_SYNC_ATOMIC_RPC_REQUIRED;

  constructor() {
    super(
      "Las ventas offline permanecen guardadas localmente hasta que la sincronización atómica esté disponible.",
    );
    this.name = "OfflineSyncUnavailableError";
  }
}

export async function queueOfflineSale(sale: OfflineSale): Promise<void> {
  const db = await getDB();
  await db.put("offline_sales", sale);
}

export async function getPendingSales(): Promise<OfflineSale[]> {
  const db = await getDB();
  return db.getAllFromIndex("offline_sales", "by-status", "pending");
}

export async function getPendingSalesByTenant(
  tenantId: string,
): Promise<OfflineSale[]> {
  const db = await getDB();
  return db.getAllFromIndex("offline_sales", "by-tenant-status", [
    tenantId,
    "pending",
  ]);
}

export async function markSaleSynced(id: string): Promise<void> {
  const db = await getDB();
  const sale = await db.get("offline_sales", id);
  if (sale) {
    await db.put("offline_sales", { ...sale, status: "synced" });
  }
}

export async function markSaleFailed(
  id: string,
  error: string,
): Promise<void> {
  const db = await getDB();
  const sale = await db.get("offline_sales", id);
  if (sale) {
    await db.put("offline_sales", {
      ...sale,
      status: "failed",
      error,
      retryCount: (sale.retryCount || 0) + 1,
    });
  }
}

export async function deleteSyncedSales(): Promise<void> {
  const db = await getDB();
  const synced = await db.getAllFromIndex(
    "offline_sales",
    "by-status",
    "synced",
  );
  const tx = db.transaction("offline_sales", "readwrite");
  for (const sale of synced) {
    await tx.store.delete(sale.id);
  }
  await tx.done;
}

function rejectUnsafeOfflineSync(): never {
  throw new OfflineSyncUnavailableError();
}

export async function retryPendingSales(): Promise<{
  success: number;
  failed: number;
}> {
  return rejectUnsafeOfflineSync();
}

export async function syncOfflineSales(_tenantId: string): Promise<void> {
  return rejectUnsafeOfflineSync();
}

// Cache helpers for offline inventory and customers
export async function cacheInventory(
  tenantId: string,
  items: any[],
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("offline_inventory", "readwrite");
  const now = Date.now();
  for (const item of items) {
    await tx.store.put({
      id: item.id,
      tenantId,
      data: item,
      cachedAt: now,
    });
  }
  await tx.done;
}

export async function getCachedInventory(tenantId: string): Promise<any[]> {
  const db = await getDB();
  const items = await db.getAllFromIndex(
    "offline_inventory",
    "by-tenant",
    tenantId,
  );
  return items.map((item) => item.data);
}

export async function cacheCustomers(
  tenantId: string,
  customers: any[],
): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("offline_customers", "readwrite");
  const now = Date.now();
  for (const customer of customers) {
    await tx.store.put({
      id: customer.id,
      tenantId,
      data: customer,
      cachedAt: now,
    });
  }
  await tx.done;
}

export async function getCachedCustomers(tenantId: string): Promise<any[]> {
  const db = await getDB();
  const items = await db.getAllFromIndex(
    "offline_customers",
    "by-tenant",
    tenantId,
  );
  return items.map((item) => item.data);
}
